import { useAuth, useAuthStrategy } from '@contexts/auth-provider';
import { HeaderOnlyLayout } from '@layouts/header-only-layout/HeaderOnlyLayout';
import { AppLoader } from '@shared/components/app-loader/AppLoader';
import { AuthErrorScreen } from '@shared/components/error-pages/auth-error/AuthErrorScreen';
import { InlineFeedback } from '@shared/components/inline-feedback/InlineFeedback';
import * as React from 'react';

import type { AuthenticatedRouteProps } from './AuthenticatedRoute.types';

/**
 * Route guard that ensures:
 * 1. User is authenticated (starts the login redirect once if not)
 * 2. Auth type matches route requirement (Train → B2C, Instruct → B2E)
 *
 * On auth type mismatch (e.g., B2E user on /train), renders a not-found page.
 * The downstream ModuleGuard also enforces module access, but this guard
 * catches auth type mismatches early with a clear error.
 */
const AuthenticatedRoute = React.memo(
  ({
    children,
    authType,
  }: Readonly<AuthenticatedRouteProps>): React.ReactElement => {
    const { isAuthenticated, isLoading } = useAuth();
    const { startLogin, hasAuthTypeMismatch } = useAuthStrategy();
    const hasStartedLoginRef = React.useRef(false);
    const [hasLoginFailed, setHasLoginFailed] = React.useState(false);
    const [isLoginPending, setIsLoginPending] = React.useState(true);

    const attemptLogin = React.useCallback((): void => {
      hasStartedLoginRef.current = true;
      setHasLoginFailed(false);
      setIsLoginPending(true);
      void startLogin(authType)
        .catch(error => {
          console.error('MSAL login failed:', error);
          setHasLoginFailed(true);
        })
        .finally(() => {
          setIsLoginPending(false);
        });
    }, [authType, startLogin]);

    const retryLogin = React.useCallback((): void => {
      hasStartedLoginRef.current = false;
      attemptLogin();
    }, [attemptLogin]);

    React.useEffect(() => {
      if (!isLoading && !isAuthenticated && !hasStartedLoginRef.current) {
        attemptLogin();
      }
    }, [isAuthenticated, isLoading, attemptLogin]);

    if (isLoading) {
      return <AppLoader />;
    }

    // Auth type mismatch: e.g., B2E user on /train route or B2C user on /instruct route.
    // Per USER_FLOWS.md, this is treated as "wrong identity provider or no access" → not-found.
    if (hasAuthTypeMismatch) {
      return (
        <HeaderOnlyLayout>
          <InlineFeedback type="not-found" />
        </HeaderOnlyLayout>
      );
    }

    if (hasLoginFailed || (!isAuthenticated && !isLoginPending)) {
      return <AuthErrorScreen onRetry={retryLogin} />;
    }

    // Login redirect in progress; AppLoader offers a reload if it never navigates away.
    if (!isAuthenticated) {
      return <AppLoader />;
    }

    return <>{children}</>;
  }
);

AuthenticatedRoute.displayName = 'AuthenticatedRoute';

export { AuthenticatedRoute };
